'use client';

import { Activity, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import Image from 'next/image';
import { ArrowUpRight, Cake, Flame, Heart, Sparkles } from 'lucide-react';
import { SiteHeader, SiteHero, SiteNavigation } from './site-chrome';
import { useSiteNavigation, type SiteNavItem } from './use-site-navigation';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from './ui/dialog';
import {
  portalCharacters as characters,
  type PortalGame,
} from '../lib/portal-data';
import {
  fetchPortalCalendar,
  fetchPortalNews,
  resolveActiveCalendarDate,
  type PortalCalendarEvent,
  type PortalNewsItem,
} from '../lib/portal-feed';
import {
  heroDirectory,
  heroUrl,
  games as registeredGames,
} from '../lib/heroes/catalog';
import { guides, wikiGame } from '../lib/wiki-data';
import './portal.css';
import PortalHome from './portal-home';
import PortalAdminEditor from './portal-admin-editor';
import { readWikiToken, readWikiUser, WIKI_AUTH_EVENT } from '../lib/wiki-auth';
import { fetchMyWikiPermission } from '../lib/wiki-permissions';
import {
  defaultPortalConfig,
  fetchPortalConfig,
  formatPortalCollected,
  resolvePortalGameLink,
  type PortalConfig,
} from '../lib/portal-config';

const UPDATES_NEWS_PAGE_SIZE = 5;
const UPDATES_CALENDAR_PAGE_SIZE = 12;
/** 游戏 Wiki 目录：桌面一行 10 个；热门取热度前 20；最新收录默认 4 行 */
const DIRECTORY_COLS = 10;
const POPULAR_DIRECTORY_LIMIT = 20;
const LATEST_DIRECTORY_PREVIEW_ROWS = 4;
const LATEST_DIRECTORY_PREVIEW =
  DIRECTORY_COLS * LATEST_DIRECTORY_PREVIEW_ROWS;

type Section = 'discover' | 'wikis' | 'updates' | 'characters';
type Preview = { title: string; text: string; image: string; label: string };
const navigation: SiteNavItem<Section>[] = [
  { id: 'discover', href: '/', title: '首页' },
  { id: 'wikis', href: '/wikis', title: '游戏 Wiki' },
  { id: 'updates', href: '/updates', title: '新游与资讯' },
  { id: 'characters', href: '/characters', title: '角色互动' },
];

export default function Portal({
  section: initialSection,
}: {
  section: Section;
}) {
  const nav = useSiteNavigation(initialSection, navigation, true);
  const section = nav.active;
  const {
    align: alignNavigation,
    go: goNavigation,
    navigate: navigateSection,
  } = nav;
  const hasBanner = section !== 'characters';
  useEffect(() => {
    document.title =
      section === 'discover'
        ? 'GAME WIKI · 游戏资料聚合站'
        : `${navigation.find((item) => item.id === section)?.title} · GAME WIKI`;
  }, [section]);
  const [query, setQuery] = useState('');
  const [directorySort, setDirectorySort] = useState<'popular' | 'latest'>(
    'popular',
  );
  const [directoryExpanded, setDirectoryExpanded] = useState(false);
  const [genre, setGenre] = useState('全部');
  const [preview, setPreview] = useState<Preview | null>(null);
  const [characterId, setCharacterId] = useState('lumi');
  const characterSelection = useRef(0);
  const scrollToCharacter = useRef(false);
  const decodedCharacters = useRef(new Map<string, Promise<void>>());
  const prepareCharacter = useCallback((src: string) => {
    const cached = decodedCharacters.current.get(src);
    if (cached) return cached;
    const image = new window.Image();
    image.decoding = 'async';
    image.src = src;
    const ready = image.decode().catch(() => {
      decodedCharacters.current.delete(src);
    });
    decodedCharacters.current.set(src, ready);
    return ready;
  }, []);
  useEffect(() => {
    // Warm only the initial portrait after the current page has had time to load.
    const timer = window.setTimeout(() => {
      void prepareCharacter(characters[0].image);
    }, 1200);
    return () => window.clearTimeout(timer);
  }, [prepareCharacter]);
  useEffect(() => {
    characterSelection.current += 1;
  }, [section]);
  useLayoutEffect(() => {
    if (!scrollToCharacter.current || section !== 'characters') return;
    scrollToCharacter.current = false;
    document.querySelector('.portal-character-scene')?.scrollIntoView({
      behavior: 'instant',
      block: 'start',
    });
  }, [characterId, section]);
  const [characterTab, setCharacterTab] = useState<'birthday' | 'arrival'>(
    'birthday',
  );
  const [blessings, setBlessings] = useState<string[]>([]);
  const [portalConfig, setPortalConfig] =
    useState<PortalConfig>(defaultPortalConfig);
  const [portalAdminOpen, setPortalAdminOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [calendarEvents, setCalendarEvents] = useState<PortalCalendarEvent[]>(
    [],
  );
  const [calendarPage, setCalendarPage] = useState(1);
  const [calendarHasMore, setCalendarHasMore] = useState(false);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [newsItems, setNewsItems] = useState<PortalNewsItem[]>([]);
  const [newsPage, setNewsPage] = useState(1);
  const [newsHasMore, setNewsHasMore] = useState(false);
  const [newsLoading, setNewsLoading] = useState(false);
  useEffect(() => {
    const syncAdmin = () => {
      if (!readWikiUser() || !readWikiToken()) {
        setIsAdmin(false);
        return;
      }
      fetchMyWikiPermission('platform')
        .then((permission) => setIsAdmin(permission.canManagePermissions))
        .catch(() => setIsAdmin(false));
    };
    syncAdmin();
    window.addEventListener(WIKI_AUTH_EVENT, syncAdmin);
    fetchPortalConfig()
      .then(setPortalConfig)
      .catch(() => undefined);
    return () => window.removeEventListener(WIKI_AUTH_EVENT, syncAdmin);
  }, []);
  useEffect(() => {
    if (section !== 'updates') return;
    let cancelled = false;
    setCalendarLoading(true);
    setNewsLoading(true);
    Promise.all([
      fetchPortalCalendar({ page: 1, pageSize: UPDATES_CALENDAR_PAGE_SIZE }),
      fetchPortalNews({ page: 1, pageSize: UPDATES_NEWS_PAGE_SIZE }),
    ])
      .then(([calendar, news]) => {
        if (cancelled) return;
        setCalendarEvents(calendar.items);
        setCalendarPage(calendar.page);
        setCalendarHasMore(calendar.hasMore);
        setNewsItems(news.items);
        setNewsPage(news.page);
        setNewsHasMore(news.hasMore);
      })
      .finally(() => {
        if (cancelled) return;
        setCalendarLoading(false);
        setNewsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [section]);
  const loadMoreCalendar = async () => {
    if (calendarLoading || !calendarHasMore) return;
    setCalendarLoading(true);
    try {
      const next = await fetchPortalCalendar({
        page: calendarPage + 1,
        pageSize: UPDATES_CALENDAR_PAGE_SIZE,
      });
      setCalendarEvents((current) => [...current, ...next.items]);
      setCalendarPage(next.page);
      setCalendarHasMore(next.hasMore);
    } finally {
      setCalendarLoading(false);
    }
  };
  const loadMoreNews = async () => {
    if (newsLoading || !newsHasMore) return;
    setNewsLoading(true);
    try {
      const next = await fetchPortalNews({
        page: newsPage + 1,
        pageSize: UPDATES_NEWS_PAGE_SIZE,
      });
      setNewsItems((current) => [...current, ...next.items]);
      setNewsPage(next.page);
      setNewsHasMore(next.hasMore);
    } finally {
      setNewsLoading(false);
    }
  };
  const games: PortalGame[] = useMemo(
    () =>
      portalConfig.games
        .filter((game) => !game.hidden)
        .map((game) => {
          const link = resolvePortalGameLink(game);
          return {
            id: game.id,
            name: game.name,
            genre: game.genre || '游戏 Wiki',
            image: game.coverUrl || game.icon,
            banner: game.banner || undefined,
            position: game.coverPosition || '50% 50%',
            description: game.summary || `${game.name} Wiki`,
            collected: formatPortalCollected(game.collectedAt),
            collectedAt: game.collectedAt,
            hot: game.hot ?? 0,
            href: link.href,
            external: link.external,
          };
        }),
    [portalConfig.games],
  );
  const popularGameIds = useMemo(() => {
    const ranked = [...games].sort((a, b) => {
      const hotDiff = (b.hot ?? 0) - (a.hot ?? 0);
      if (hotDiff !== 0) return hotDiff;
      return (b.collectedAt || '').localeCompare(a.collectedAt || '');
    });
    return new Set(
      ranked.slice(0, POPULAR_DIRECTORY_LIMIT).map((game) => game.id),
    );
  }, [games]);
  useEffect(() => {
    setDirectoryExpanded(false);
  }, [directorySort, genre, query]);
  const activeCalendarDate = useMemo(
    () => resolveActiveCalendarDate(calendarEvents),
    [calendarEvents],
  );
  const calendarMonthLabel = useMemo(() => {
    const raw = activeCalendarDate || calendarEvents[0]?.eventDate || '';
    const match = raw.match(/^(\d{4})-(\d{2})/);
    return match ? `${match[1]} / ${match[2]}` : '近期日程';
  }, [activeCalendarDate, calendarEvents]);
  const keyword = query.trim().toLowerCase();
  const character = characters.find((item) => item.id === characterId)!;
  const matches = (value: string) => value.toLowerCase().includes(keyword);
  const filteredGames = games.filter(
    (game) =>
      matches(`${game.name} ${game.genre}`) &&
      (genre === '全部' || game.genre.includes(genre)),
  );
  const directoryGames = useMemo(() => {
    const pool = filteredGames.filter((game) =>
      directorySort === 'popular'
        ? popularGameIds.has(game.id)
        : !popularGameIds.has(game.id),
    );
    return [...pool].sort((a, b) => {
      if (directorySort === 'popular') {
        const hotDiff = (b.hot ?? 0) - (a.hot ?? 0);
        if (hotDiff !== 0) return hotDiff;
      }
      return (b.collectedAt || '').localeCompare(a.collectedAt || '');
    });
  }, [directorySort, filteredGames, popularGameIds]);
  const directoryGamesVisible =
    directorySort === 'latest' && !directoryExpanded
      ? directoryGames.slice(0, LATEST_DIRECTORY_PREVIEW)
      : directoryGames;
  const directoryCanExpand =
    directorySort === 'latest' &&
    !directoryExpanded &&
    directoryGames.length > LATEST_DIRECTORY_PREVIEW;
  const searchHeroes = keyword
    ? heroDirectory.filter((hero) =>
        matches(`${hero.name} ${registeredGames[hero.gameId]?.name || ''}`),
      )
    : [];
  const searchGuides = keyword
    ? guides.filter((guide) =>
        matches(`${guide.title} ${guide.category} ${wikiGame.name}`),
      )
    : [];
  const searchCharacters = keyword
    ? characters.filter((item) => matches(`${item.name} ${item.game}`))
    : [];
  const searchCount =
    filteredGames.length +
    searchHeroes.length +
    searchGuides.length +
    searchCharacters.length;
  const renderGame = (game: PortalGame) => {
    const content = (
      <>
        <Image
          unoptimized
          width={88}
          height={88}
          src={game.image}
          alt=""
          style={{ objectPosition: game.position }}
        />
        <h3>{game.name}</h3>
      </>
    );
    return game.href ? (
      <a
        className="portal-game-tile"
        key={game.id}
        href={game.href}
        {...(game.external
          ? { target: '_blank', rel: 'noopener noreferrer' }
          : {})}
      >
        {content}
      </a>
    ) : (
      <button
        className="portal-game-tile"
        key={game.id}
        onClick={() => gamePreview(game)}
      >
        {content}
      </button>
    );
  };
  const gamePreview = useCallback(
    (game: PortalGame) =>
      setPreview({
        title: game.name,
        text: `${game.description}\n\n这是虚构游戏的展示页，Wiki 尚未开放。`,
        image: game.image,
        label: `${game.genre} · 虚构游戏`,
      }),
    [],
  );
  const orderedCharacters = [...characters].sort((a, b) =>
    characterTab === 'birthday'
      ? a.birthday.localeCompare(b.birthday)
      : b.arrival.localeCompare(a.arrival),
  );
  const blessed = blessings.includes(character.id);
  const handleSearch = useCallback(() => {
      if (section !== 'characters') alignNavigation();
      else goNavigation('discover', 'content');
  }, [alignNavigation, goNavigation, section]);
  const search = useMemo(
    () => ({
      query,
      onQuery: setQuery,
      placeholder: '搜索游戏、角色、攻略',
      onSearch: handleSearch,
    }),
    [handleSearch, query],
  );
  const handleNavigate = useCallback(
    (event: Parameters<typeof nav.navigate>[0], id: Section) => {
      if (navigateSection(event, id)) setQuery('');
    },
    [navigateSection],
  );
  const sectionNavigation = (
    <SiteNavigation
      items={navigation}
      active={section}
      compact={nav.compact}
      marker={nav.marker}
      onNavigate={handleNavigate}
      {...search}
    />
  );
  const heroChildren = useMemo(
    () =>
      (
        <>
          <div className="portal-platform-stats" aria-label="平台收录统计">
            <span>
              收录游戏 <strong>{games.length}</strong> 款
            </span>
            <span>
              已建 Wiki <strong>{games.filter((game) => game.href).length}</strong>{' '}
              个
            </span>
            <span>
              角色图鉴 <strong>{heroDirectory.length}</strong> 个
            </span>
          </div>
          <p className="portal-stats-note">按当前可访问内容统计 · 不含虚构演示条目</p>
        </>
      ),
    [games],
  );
  const searchPanel = keyword ? (
    <section className="portal-search-popover" aria-label="搜索结果">
      <div className="portal-search-popover-heading">
        <span>搜索结果</span>
        <button type="button" onClick={() => setQuery('')}>
          清除
        </button>
      </div>
      <div className="portal-search-popover-list">
        {filteredGames.slice(0, 3).map((game) =>
          game.href ? (
            <a
              key={`game-${game.id}`}
              href={game.href}
              {...(game.external
                ? { target: '_blank', rel: 'noopener noreferrer' }
                : {})}
            >
              <span>{game.name}</span>
              <small>游戏 Wiki</small>
            </a>
          ) : (
            <button
              type="button"
              key={`game-${game.id}`}
              onClick={() => {
                setQuery('');
                gamePreview(game);
              }}
            >
              <span>{game.name}</span>
              <small>游戏</small>
            </button>
          ),
        )}
        {searchHeroes.slice(0, 3).map((hero) => (
          <a key={`hero-${hero.gameId}-${hero.id}`} href={heroUrl(hero)}>
            <span>{hero.name}</span>
            <small>{registeredGames[hero.gameId]?.name || '角色图鉴'}</small>
          </a>
        ))}
        {searchGuides.slice(0, 2).map((guide) => (
          <a key={`guide-${guide.id}`} href={`/${wikiGame.key}/${guide.id}`}>
            <span>{guide.title}</span>
            <small>{guide.category}</small>
          </a>
        ))}
        {searchCharacters.slice(0, 2).map((item) => (
          <button
            type="button"
            key={`character-${item.id}`}
            onClick={() => {
              setQuery('');
              setPreview({
                title: item.name,
                text: item.description,
                image: item.image,
                label: `${item.game} · 虚构角色`,
              });
            }}
          >
            <span>{item.name}</span>
            <small>{item.game}</small>
          </button>
        ))}
        {searchCount === 0 && <p>没有找到相关内容</p>}
      </div>
      {searchCount > 0 && (
        <output>共找到 {searchCount} 项，当前显示部分结果</output>
      )}
    </section>
  ) : (
    <section
      className="portal-search-popover portal-search-popover-hot"
      aria-label="热门搜索"
    >
      <div className="portal-search-popover-heading">
        <span>热门搜索</span>
      </div>
      <div className="portal-search-hot-list">
        {games.slice(0, 6).map((game, index) => (
          <button
            type="button"
            key={`hot-search-${game.id}`}
            onClick={() => setQuery(game.name)}
          >
            <span>
              {game.name}
              {index < 3 && <Flame size={15} aria-hidden="true" />}
            </span>
            <small>{game.genre}</small>
          </button>
        ))}
      </div>
    </section>
  );

  return (
    <div
      className={`portal portal-${section}${hasBanner ? ' portal-with-banner' : ''}`}
    >
      <SiteHeader
        banner={hasBanner}
        compact={nav.compact}
        search={search}
        searchPanel={searchPanel}
      />
      <main>
        {!hasBanner && sectionNavigation}
        {hasBanner && (
          <>
            <SiteHero
              title="你的游戏资料库"
              eyebrow="GAME WIKI"
              compact={nav.compact}
              {...search}
            >
              {heroChildren}
            </SiteHero>
            {sectionNavigation}
          </>
        )}
            <Activity mode={section === 'discover' ? 'visible' : 'hidden'}>
              <PortalHome
                games={games}
                onGame={gamePreview}
                onPreview={setPreview}
                onOpenWikis={(sort) => {
                  setDirectorySort(sort);
                  setDirectoryExpanded(false);
                  setGenre('全部');
                  goNavigation('wikis', 'content');
                }}
                onOpenUpdates={() => goNavigation('updates', 'content')}
                onOpenCharacters={() => goNavigation('characters', 'content')}
              />
            </Activity>
            <Activity mode={section === 'wikis' ? 'visible' : 'hidden'}>
              <section className="portal-width portal-directory">
                <div className="portal-section-heading">
                  <h2>全部游戏</h2>
                  <div className="portal-tabs">
                    <button
                      aria-pressed={directorySort === 'popular'}
                      onClick={() => setDirectorySort('popular')}
                    >
                      热门
                    </button>
                    <button
                      aria-pressed={directorySort === 'latest'}
                      onClick={() => setDirectorySort('latest')}
                    >
                      最新收录
                    </button>
                  </div>
                </div>
                {section === 'wikis' && (
                  <div className="portal-genre-filter" aria-label="游戏分类">
                    {['全部', '策略', 'RPG', '开放世界', '冒险'].map((item) => (
                      <button
                        key={item}
                        aria-pressed={genre === item}
                        onClick={() => setGenre(item)}
                      >
                        {item}
                      </button>
                    ))}
                  </div>
                )}
                <div className="portal-tile-grid">
                  {directoryGamesVisible.map(renderGame)}
                </div>
                {section === 'wikis' && directoryCanExpand && (
                  <div className="portal-directory-more">
                    <button type="button" onClick={() => setDirectoryExpanded(true)}>
                      显示更多
                    </button>
                  </div>
                )}
                {section === 'wikis' && directoryGames.length === 0 && (
                  <div className="portal-empty">
                    <p>没有找到符合条件的游戏。</p>
                    <button
                      onClick={() => {
                        setQuery('');
                        setGenre('全部');
                      }}
                    >
                      清除筛选
                    </button>
                  </div>
                )}
              </section>
            </Activity>

        <Activity mode={section === 'updates' ? 'visible' : 'hidden'}>
          <div className="portal-width portal-updates-content">
            <section className="portal-news" aria-labelledby="portal-hot-heading">
              <div className="portal-section-heading">
                <div>
                  <p className="portal-eyebrow">HOT</p>
                  <h2 id="portal-hot-heading">最新热点</h2>
                </div>
                <span>今日值得留意</span>
              </div>
              <div className="portal-news-list">
                {newsItems.map((item) => (
                  <button
                    className="portal-news-row"
                    key={item.id}
                    onClick={() =>
                      setPreview({
                        title: item.title,
                        text: item.body,
                        image: item.coverUrl,
                        label: `${item.tag || '资讯'} · ${item.date}`,
                      })
                    }
                  >
                    <Image
                      unoptimized
                      width={320}
                      height={180}
                      className="portal-news-thumb"
                      src={item.coverUrl}
                      alt=""
                      loading="lazy"
                    />
                    <div className="portal-news-meta">
                      <time>{item.date}</time>
                      <span>{item.tag || '资讯'}</span>
                    </div>
                    <h3>{item.title}</h3>
                    <ArrowUpRight size={18} aria-hidden />
                  </button>
                ))}
              </div>
              {newsHasMore && (
                <div className="portal-empty portal-updates-more">
                  <button onClick={loadMoreNews} disabled={newsLoading}>
                    {newsLoading ? '加载中…' : '加载更多资讯'}
                  </button>
                </div>
              )}
            </section>

            <section className="portal-events" aria-labelledby="portal-cal-heading">
              <div className="portal-section-heading">
                <div>
                  <p className="portal-eyebrow">CALENDAR</p>
                  <h2 id="portal-cal-heading">游戏日历</h2>
                </div>
                <span>{calendarMonthLabel}</span>
              </div>
              <div className="portal-timeline">
                {calendarEvents.map((event, index) => {
                  const isCurrent = event.eventDate === activeCalendarDate;
                  return (
                    <button
                      key={event.id}
                      className={`portal-cal-card portal-cal-card-${index % 4}${isCurrent ? ' is-current' : ''}`}
                      onClick={() =>
                        setPreview({
                          title: `${event.gameName} · ${event.eventType}`,
                          text: event.detail,
                          image: event.coverUrl,
                          label: `${event.eventDate.replace(/-/g, '.')} · 游戏日程`,
                        })
                      }
                    >
                      <time dateTime={event.eventDate}>
                        {event.month}.{event.day}
                        <i />
                      </time>
                      <Image
                        unoptimized
                        width={320}
                        height={200}
                        src={event.coverUrl}
                        alt=""
                        loading="lazy"
                      />
                      <h3>{event.gameName}</h3>
                      <span className="portal-cal-tag">{event.eventType}</span>
                      <p>{event.title}</p>
                    </button>
                  );
                })}
              </div>
              {calendarHasMore && (
                <div className="portal-empty portal-updates-more">
                  <button onClick={loadMoreCalendar} disabled={calendarLoading}>
                    {calendarLoading ? '加载中…' : '加载更多日程'}
                  </button>
                </div>
              )}
            </section>

            <section className="portal-latest" aria-labelledby="portal-latest-heading">
              <div className="portal-section-heading">
                <div>
                  <p className="portal-eyebrow">WIKI</p>
                  <h2 id="portal-latest-heading">最新收录</h2>
                </div>
                <span>最近加入的 Wiki</span>
              </div>
              <div className="portal-icon-row">
                {[...games]
                  .filter((game) => !popularGameIds.has(game.id))
                  .sort((a, b) =>
                    (b.collectedAt || '').localeCompare(a.collectedAt || ''),
                  )
                  .map((game) => {
                    const content = (
                      <>
                        <Image
                          unoptimized
                          width={1600}
                          height={900}
                          src={game.image}
                          alt=""
                          loading="lazy"
                          style={{ objectPosition: game.position }}
                        />
                        <b>{game.name}</b>
                        <small>
                          {game.collected ? `${game.collected} 收录` : '\u00a0'}
                        </small>
                      </>
                    );
                    return game.href ? (
                      <a
                        key={game.id}
                        href={game.href}
                        {...(game.external
                          ? { target: '_blank', rel: 'noopener noreferrer' }
                          : {})}
                      >
                        {content}
                      </a>
                    ) : (
                      <button key={game.id} onClick={() => gamePreview(game)}>
                        {content}
                      </button>
                    );
                  })}
              </div>
            </section>
          </div>
        </Activity>

        <Activity mode={section === 'characters' ? 'visible' : 'hidden'}>
          <>
            <section className="portal-character-scene" aria-label="角色展示">
              <Image
                unoptimized
                width={1600}
                height={900}
                className="portal-scene"
                src={character.image}
                alt={`${character.name}角色插画`}
                style={{ objectPosition: character.position }}
                fetchPriority="high"
                loading="eager"
                decoding="async"
              />
              <div className="portal-spotlight-shade" />
              <div
                className="portal-character-copy"
              >
                <p className="portal-eyebrow">
                  {character.game} <span> / {character.title}</span>
                </p>
                <span className="portal-character-latin">
                  {character.latin}
                </span>
                <h1>{character.name}</h1>
                <p className="portal-character-description">
                  {character.description}
                </p>
                <div className="portal-character-date">
                  {characterTab === 'birthday' ? (
                    <Cake size={18} />
                  ) : (
                    <Sparkles size={18} />
                  )}
                  <span>
                    {characterTab === 'birthday'
                      ? `${character.birthday} 生日`
                      : `${character.arrival} 实装`}
                  </span>
                </div>
                <button
                  className={`portal-primary ${blessed ? 'is-blessed' : ''}`}
                  aria-pressed={blessed}
                  onClick={() =>
                    setBlessings((value) =>
                      blessed
                        ? value.filter((id) => id !== character.id)
                        : [...value, character.id],
                    )
                  }
                >
                  <Heart size={17} fill={blessed ? 'currentColor' : 'none'} />
                  {blessed
                    ? '已送出心意'
                    : characterTab === 'birthday'
                      ? '送上生日祝福'
                      : '为角色点赞'}
                </button>
              </div>
            </section>
            <section className="portal-width portal-character-collection">
              <div className="portal-section-heading">
                <div>
                  <p className="portal-eyebrow">CHARACTER ARCHIVE</p>
                  <h2>每一次相遇，都值得记住。</h2>
                </div>
                <div className="portal-tabs" aria-label="角色列表分类">
                  <button
                    aria-pressed={characterTab === 'birthday'}
                    onClick={() => setCharacterTab('birthday')}
                  >
                    近期生日
                  </button>
                  <button
                    aria-pressed={characterTab === 'arrival'}
                    onClick={() => setCharacterTab('arrival')}
                  >
                    最近实装
                  </button>
                </div>
              </div>
              <div className="portal-character-grid">
                {orderedCharacters.map((item) => (
                  <button
                    key={item.id}
                    aria-pressed={characterId === item.id}
                    onPointerEnter={() => { void prepareCharacter(item.image); }}
                    onFocus={() => { void prepareCharacter(item.image); }}
                    onClick={async () => {
                      const request = ++characterSelection.current;
                      await prepareCharacter(item.image);
                      if (request !== characterSelection.current) return;
                      if (characterId === item.id) {
                        document.querySelector('.portal-character-scene')?.scrollIntoView({ behavior: 'instant' });
                        return;
                      }
                      scrollToCharacter.current = true;
                      setCharacterId(item.id);
                    }}
                  >
                    <div>
                      <Image
                        unoptimized
                        width={1600}
                        height={900}
                        src={item.image}
                        alt={item.name}
                        loading="lazy"
                        style={{ objectPosition: item.position }}
                      />
                      <span>
                        查看角色 <ArrowUpRight size={16} />
                      </span>
                    </div>
                    <h3>{item.name}</h3>
                    <p>
                      {item.game}
                      <time>
                        {characterTab === 'birthday'
                          ? item.birthday
                          : item.arrival}
                      </time>
                    </p>
                  </button>
                ))}
              </div>
            </section>
          </>
        </Activity>
      </main>
      <footer className="portal-footer">
        <a href="/" className="portal-brand">
          <b>GW</b>
          <span>GAME WIKI</span>
        </a>
        <p>部分游戏、资讯与角色为虚构演示；阿尔克纳 Wiki 可访问。</p>
        <span>发现 · 记录 · 相遇</span>
      </footer>
      <Dialog
        open={preview !== null}
        onOpenChange={(open) => {
          if (!open) setPreview(null);
        }}
      >
        <DialogContent className="portal-preview">
          {preview && (
            <>
              <Image
                unoptimized
                width={1600}
                height={900}
                src={preview.image}
                alt=""
              />
              <div className="portal-preview-body">
                <p>{preview.label}</p>
                <DialogTitle>{preview.title}</DialogTitle>
                <DialogDescription>{preview.text}</DialogDescription>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
      {isAdmin && (
        <button
          className="portal-admin-trigger"
          type="button"
          onClick={() => setPortalAdminOpen(true)}
        >
          首页管理
        </button>
      )}
      <PortalAdminEditor
        config={portalConfig}
        open={portalAdminOpen}
        onClose={() => setPortalAdminOpen(false)}
        onChange={setPortalConfig}
      />
    </div>
  );
}
